import KPICard from '../common/KPICard';
import { formatCurrency, formatPercent } from '../../utils/formatting';

export default function KPIRow({ kpi, targets, annualSummary }) {
  const current2026 = annualSummary.find(d => d.year === 2026);
  const actual2025 = annualSummary.find(d => d.year === 2025);

  // Projected revenue and YoY growth
  const projectedRevenue = kpi.projected_revenue_2026;
  const yoyGrowth = actual2025
    ? (projectedRevenue - actual2025.revenue) / actual2025.revenue
    : 0;
  // Status: on track if projected > 90% of target
  const revenueStatus = projectedRevenue >= targets.revenue_target * 0.9 ? 'ok' : projectedRevenue >= targets.revenue_target * 0.75 ? 'warn' : 'danger';
  const revenuePctOfTarget = projectedRevenue / targets.revenue_target;

  // Gross Profit %
  const gpPct = current2026 ? (current2026.revenue - current2026.total_cogs) / current2026.revenue : 0;
  const gpStatus = gpPct >= targets.gross_profit_pct ? 'ok' : gpPct >= targets.gross_profit_pct * 0.8 ? 'warn' : 'danger';
  const gpGap = gpPct - targets.gross_profit_pct;

  // Net Profit %
  const npPct = current2026 ? current2026.net_income / current2026.revenue : 0;
  const npStatus = npPct >= targets.net_profit_pct ? 'ok' : npPct >= targets.net_profit_pct * 0.5 ? 'warn' : 'danger';

  // Net Income
  const netIncome = kpi.projected_net_income_2026;
  const netIncome2025 = actual2025?.net_income || 0;
  const noiYoY = netIncome2025 ? (netIncome - netIncome2025) / Math.abs(netIncome2025) : 0;

  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <KPICard
        label="2026 Projected Revenue"
        value={formatCurrency(projectedRevenue)}
        subtitle={`${formatPercent(revenuePctOfTarget)} of ${formatCurrency(targets.revenue_target)} target | +${formatPercent(yoyGrowth)} vs 2025`}
        status={revenueStatus}
        statusLabel={revenueStatus === 'ok' ? 'On Track' : revenueStatus === 'warn' ? 'Monitor' : 'Behind'}
      />
      <KPICard
        label="Gross Profit Margin"
        value={formatPercent(gpPct)}
        subtitle={`Target: ${formatPercent(targets.gross_profit_pct)} | ${gpGap >= 0 ? '+' : ''}${formatPercent(gpGap)} gap`}
        status={gpStatus}
        statusLabel={gpStatus === 'ok' ? 'On Target' : gpStatus === 'warn' ? 'Below Target' : 'Critical'}
      />
      <KPICard
        label="Net Profit Margin"
        value={formatPercent(npPct)}
        subtitle={`Target: ${formatPercent(targets.net_profit_pct)} | ${formatCurrency(netIncome)} projected NOI`}
        status={npStatus}
        statusLabel={npStatus === 'ok' ? 'On Target' : npStatus === 'warn' ? 'Below Target' : 'Critical'}
      />
      <FpRetentionCard kpi={kpi} />
    </div>
  );
}

// Separate component so the "no data yet" guard is explicit rather than
// rendering 100% on an empty client cache (the old bug).
function FpRetentionCard({ kpi }) {
  const universeEmpty = !kpi.fp_clients_original || kpi.fp_clients_original === 0;
  const rate = universeEmpty ? null : kpi.fp_clients_retained / kpi.fp_clients_original;
  return (
    <KPICard
      label="FP Client Retention"
      value={universeEmpty ? '—' : formatPercent(rate)}
      subtitle={
        universeEmpty
          ? 'Loading client data...'
          : `${kpi.fp_clients_retained} of ${kpi.fp_clients_original} cohort retained | ${kpi.fp_clients_total} active today`
      }
      status={universeEmpty ? 'warn' : rate >= 0.90 ? 'ok' : 'warn'}
      statusLabel={universeEmpty ? 'No Data' : rate >= 0.90 ? 'Healthy' : 'At Risk'}
    />
  );
}
